import {
  Download,
  FileText,
  Package,
  Printer,
  Recycle,
  Warning,
} from '@phosphor-icons/react';
import styled from 'styled-components';
import { TableCell, TableRow } from 'semantic-ui-react';
import { assignPackageItems } from '../../../utility/packaging/package-items';
import { useMemo } from 'react';
import { HeaderText, MuteText, ShadowBox, StyledButton } from '../../../elements';
import { CommonUtility, theme } from '../../../utility';
import {
  ActionEditDots,
  ActionMenuItem,
  AppTable,
  TextCopyToClipboard,
} from '../../../components';
import { useDebouncedEffect } from '../../../hooks';
import { Tooltip } from 'antd';
import { IconSizes } from '@utility/constants/style-contants';

const StyledShadowBox = styled(ShadowBox)`
  flex: 1;
  margin-left: 10px;
`;

const PlaceholderContainer = styled.div`
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

function LabelRow({ item }) {
  return (
    <TableRow>
      <TableCell>
        <TextCopyToClipboard text={item.tracking || item.tracking_id} />
      </TableCell>
      <TableCell>{item.carrier}</TableCell>
      <TableCell>{CommonUtility.currencyFormat(item.rate)}</TableCell>
      <TableCell>
        <div className="text-nowrap">
          <span>{CommonUtility.roundNumber(item.dimensions?.length?.value)}</span>
          <MuteText className="mx-1">x</MuteText>
          <span>{CommonUtility.roundNumber(item.dimensions?.height?.value)}</span>
          <MuteText className="mx-1">x</MuteText>
          <span>{CommonUtility.roundNumber(item.dimensions?.width?.value)}</span>
        </div>
      </TableCell>
      <TableCell>
        {CommonUtility.roundNumber(item.weight?.pounds || 0)} <MuteText>lb</MuteText>{' '}
        {CommonUtility.roundNumber(item.weight?.ounces || 0)} <MuteText>oz</MuteText>
      </TableCell>
    </TableRow>
  );
}

export function doesPackageLabelMatchPackedItems(pack) {
  if (pack.identityError) return false;
  if (!pack.label) return true;
  const assigned = assignPackageItems([{ items: pack.label.items }], pack.packedItems);
  return !assigned.packages[0].identityError && assigned.remaining.length === 0;
}

export function PackageLabel({
  pack,
  index,
  openLabelGenerationForPackage,
  onOpenPackageCorrection,
  hasShipNode,
  askToPrint,
  setLabelToRefund,
  fileName,
  readOnly,
}) {
  const label = pack?.label;

  const isOld = useMemo(() => {
    // Unknown assignment is not proof that a purchased label should be voided.
    return !!pack.label && !pack.identityError && !doesPackageLabelMatchPackedItems(pack);
  }, [pack]);

  useDebouncedEffect(
    () => {
      if (isOld && pack.label) {
        setLabelToRefund(label);
      }
    },
    [isOld, pack],
    100,
  );

  const doesPackedItemsAndLabelMatch = useMemo(() => {
    return doesPackageLabelMatchPackedItems(pack);
  }, [pack]);

  const columns = useMemo(
    () => [
      {
        text: 'Tracking',
        key: 'tracking',
        textAlign: 'left',
        width: 4,
      },
      {
        text: 'Carrier',
        key: 'carrier',
        textAlign: 'left',
        width: 1,
      },
      {
        text: 'Price',
        key: 'price',
        textAlign: 'left',
        width: 2,
      },
      {
        text: `Dimensions (in.)`,
        key: 'dimensions',
        textAlign: 'left',
        width: 3,
      },
      {
        text: 'Weight',
        key: 'weight',
        textAlign: 'left',
        width: 2,
      },
    ],
    [pack],
  );

  return (
    <StyledShadowBox className="h-100 col-6">
      <div className="d-flex justify-content-between">
        <div className="d-flex align-items-center">
          <FileText size={24} color={theme.colors.grey} />
          <HeaderText className="mx-1">
            {label?.url
              ? `Package ${index + 1} Labels`
              : `Generate Label for Package ${index + 1}`}
          </HeaderText>
          {!doesPackedItemsAndLabelMatch && (
            <Tooltip title="Packed items and label do not match, please regenerate the label">
              <Warning size={IconSizes.medium} color={theme.colors.red} />
            </Tooltip>
          )}
        </div>
        {label && (
          <ActionEditDots
            preventChangingBg
            on="hover"
            position="bottom left"
            inline
            showByDefault
            smallBtn
          >
            <ActionMenuItem
              onClick={() => askToPrint(label.url, `${fileName}- Package ${index + 1}`)}
            >
              {hasShipNode ? (
                <>
                  <Printer />
                  Print
                </>
              ) : (
                <>
                  <Download />
                  Download Label
                </>
              )}
            </ActionMenuItem>
            {!readOnly && (
              <ActionMenuItem onClick={() => setLabelToRefund(label)}>
                <Recycle />
                Void
              </ActionMenuItem>
            )}
            {!readOnly && (
              <ActionMenuItem onClick={() => onOpenPackageCorrection?.(pack, index)}>
                <Package />
                Change Package
              </ActionMenuItem>
            )}
          </ActionEditDots>
        )}
      </div>
      {label?.url ? (
        <AppTable data={[label]} RowComponent={<LabelRow />} columns={columns} noShadow />
      ) : (
        <PlaceholderContainer>
          <StyledButton onClick={openLabelGenerationForPackage}>
            Generate Label for Package {index + 1}
          </StyledButton>
        </PlaceholderContainer>
      )}
    </StyledShadowBox>
  );
}
